import { Controller } from "@hotwired/stimulus"

import { DEFAULT_FRAME_TIMEOUT_MS, armFrameLoadTimeout } from "../frame_load_timeout"

// Expand/collapse a course row and lazy-load nested students on first expand.
// Controller is on the expand <button>; the panel <tr> is found via aria-controls.
export default class extends Controller {
  static targets = ["chevronCollapsed", "chevronExpanded"]
  static values = {
    studentsUrl: String,
    timeoutMs: { type: Number, default: DEFAULT_FRAME_TIMEOUT_MS },
  }

  connect() {
    this.clearLoadTimeout = null
    this.loadGeneration = 0
    this.listenedFrame = null
    this.boundFrameLoad = this.onFrameLoad.bind(this)
    this.boundFrameMissing = this.onFrameMissing.bind(this)
    this.boundFrameFetchError = this.onFrameFetchError.bind(this)
    this.boundRetryClick = this.onRetryClick.bind(this)
    this.panelElement()?.addEventListener("click", this.boundRetryClick)
    this.ensureFrameListeners()
    this.sync()
  }

  disconnect() {
    this.cancelLoadTimeout()
    this.removeFrameListeners()
    this.panelElement()?.removeEventListener("click", this.boundRetryClick)
  }

  ensureFrameListeners() {
    const frame = this.frameElement()
    if (!frame || frame === this.listenedFrame) return

    this.removeFrameListeners()
    this.listenedFrame = frame
    frame.addEventListener("turbo:frame-load", this.boundFrameLoad)
    frame.addEventListener("turbo:frame-missing", this.boundFrameMissing)
    frame.addEventListener("turbo:fetch-request-error", this.boundFrameFetchError)
  }

  removeFrameListeners() {
    if (!this.listenedFrame) return

    this.listenedFrame.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    this.listenedFrame.removeEventListener("turbo:frame-missing", this.boundFrameMissing)
    this.listenedFrame.removeEventListener(
      "turbo:fetch-request-error",
      this.boundFrameFetchError,
    )
    this.listenedFrame = null
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const panel = this.panelElement()
    if (!panel) return

    if (panel.hasAttribute("hidden")) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  expand() {
    const panel = this.panelElement()
    if (!panel) return

    // Inject skeleton before revealing the panel so the first paint is never an
    // empty nested table while the students request is in flight.
    this.ensureFrameListeners()
    this.loadStudentsIfNeeded()
    panel.removeAttribute('hidden')
    this.sync()
  }

  collapse() {
    const panel = this.panelElement()
    if (!panel) return

    this.cancelLoadTimeout()
    this.loadGeneration += 1

    const frame = this.frameElement()
    if (frame) {
      frame.removeAttribute("src")
      frame.setAttribute("aria-busy", "false")
      frame.dataset.loadGeneration = String(this.loadGeneration)
    }

    panel.setAttribute('hidden', '')
    this.sync()
  }

  loadStudentsIfNeeded() {
    const frame = this.frameElement()
    if (!frame || this.hasHydratedStudents(frame) || this.isStudentsLoading(frame)) return

    const url = this.studentsUrlValue
    if (!url) return

    frame.setAttribute("aria-busy", "true")
    this.loadGeneration += 1
    frame.dataset.loadGeneration = String(this.loadGeneration)
    frame.innerHTML = this.skeletonMarkup()
    frame.setAttribute("src", url)
    this.armLoadTimeout(frame)
  }

  armLoadTimeout(frame) {
    this.cancelLoadTimeout()
    this.clearLoadTimeout = armFrameLoadTimeout({
      timeoutMs: this.timeoutMsValue,
      onTimeout: () => this.showFrameLoadError(frame),
    })
  }

  cancelLoadTimeout() {
    if (this.clearLoadTimeout) {
      this.clearLoadTimeout()
      this.clearLoadTimeout = null
    }
  }

  onFrameLoad(event) {
    if (event.target !== this.frameElement()) return

    const generation = Number(event.target.dataset.loadGeneration)
    if (!generation || generation !== this.loadGeneration) return

    this.cancelLoadTimeout()
    event.target.setAttribute("aria-busy", "false")
    this.restoreFocusAfterAsyncUpdate()
  }

  hasHydratedStudents(frame) {
    if (frame.querySelector("[data-educator-home-page-course-monitoring-students-empty]")) return true

    if (frame.querySelector("[data-educator-home-page-course-monitoring-students-skeleton]")) return false

    if (
      frame.querySelector("[data-educator-home-page-course-monitoring-students-error]") ||
      frame.querySelector("[role='alert']")
    ) {
      return false
    }

    if (frame.querySelector(".aqui-base-table__grid")) return true

    return frame.innerHTML.trim().length > 0 && frame.getAttribute("aria-busy") !== "true"
  }

  isStudentsLoading(frame) {
    return (
      frame.getAttribute("aria-busy") === "true" ||
      frame.querySelector("[data-educator-home-page-course-monitoring-students-skeleton]") != null
    )
  }

  onFrameMissing(event) {
    if (event.target !== this.frameElement()) return

    event.preventDefault()
    this.showFrameLoadError(event.target)
  }

  onFrameFetchError(event) {
    if (event.target !== this.frameElement()) return

    event.preventDefault()
    this.showFrameLoadError(event.target)
  }

  showFrameLoadError(frame) {
    this.loadGeneration += 1
    this.cancelLoadTimeout()
    frame.setAttribute("aria-busy", "false")
    frame.removeAttribute("src")
    frame.innerHTML = this.frameErrorMarkup()

    const retry = frame.querySelector("[data-educator-home-page-course-monitoring-students-retry]")
    retry?.focus()
  }

  onRetryClick(event) {
    const retry = event.target.closest("[data-educator-home-page-course-monitoring-students-retry]")
    if (!retry) return

    const frame = this.frameElement()
    if (!frame || !frame.contains(retry)) return
    if (this.element.closest("[inert]")) return

    event.preventDefault()
    this.element.focus()
    this.loadStudentsIfNeeded()
  }

  restoreFocusAfterAsyncUpdate() {
    if (this.element.closest("[inert]")) return

    const active = document.activeElement
    if (active === this.element) return

    const retryWasFocused = active?.matches?.("[data-educator-home-page-course-monitoring-students-retry]")
    const focusLost = !active || active === document.body || active === document.documentElement
    if (!retryWasFocused && !focusLost) return

    if (this.element.getAttribute("aria-expanded") === "true") {
      this.element.focus()
    }
  }

  frameErrorMarkup() {
    const panel = this.panelElement()
    const template = panel?.querySelector(
      "template[data-educator-home-page-course-monitoring-students-error-template]"
    )
    if (template) return template.innerHTML

    return ""
  }

  sync() {
    const panel = this.panelElement()
    const expanded = panel ? !panel.hasAttribute('hidden') : false
    const panelId = this.element.getAttribute("aria-controls")

    this.expandControlsFor(panelId).forEach((control) => {
      control.setAttribute("aria-expanded", expanded ? "true" : "false")
      control.setAttribute(
        "aria-label",
        expanded ? this.collapseLabel(control) : this.expandLabel(control)
      )

      const collapsedChevron = control.querySelector(
        "[data-educator-home-page--course-monitoring-expand-row-target='chevronCollapsed']"
      )
      const expandedChevron = control.querySelector(
        "[data-educator-home-page--course-monitoring-expand-row-target='chevronExpanded']"
      )

      if (collapsedChevron) {
        collapsedChevron.hidden = expanded
      }
      if (expandedChevron) {
        expandedChevron.hidden = !expanded
      }
    })

    const courseRow = this.element.closest("tr")
    if (!courseRow) return

    if (expanded) {
      courseRow.dataset.expanded = "true"
    } else {
      courseRow.removeAttribute("data-expanded")
    }
  }

  expandControlsFor(panelId) {
    if (!panelId) return [this.element]

    return Array.from(
      document.querySelectorAll(`button[aria-controls="${panelId}"]`)
    )
  }

  panelElement() {
    const panelId = this.element.getAttribute("aria-controls")
    if (!panelId) return null

    return document.getElementById(panelId)
  }

  frameElement() {
    const panel = this.panelElement()
    if (!panel) return null

    return panel.querySelector("turbo-frame")
  }

  expandLabel(control = this.element) {
    return control.dataset.expandLabel || "Expand course students"
  }

  collapseLabel(control = this.element) {
    return control.dataset.collapseLabel || "Collapse course students"
  }

  // Server-rendered templates live in the expand panel (outside this controller).
  // Prefer those over JS-built markup so skeleton/error match hydrated SSR chrome.
  skeletonMarkup() {
    const panel = this.panelElement()
    const template = panel?.querySelector(
      "template[data-educator-home-page-course-monitoring-students-skeleton-template]"
    )
    if (template) return template.innerHTML

    return ""
  }
}
